/**
 * Google Analytics 4 — по доке UA-команды, ради тестирования рекламы в Google Ads.
 *
 * Той же архитектуры, что pixels.ts: сырой gtag-снипет без SDK, полный no-op пока не задан
 * VITE_GA4_ID (Measurement ID вида G-XXXXXXX — в доке его нет, выдаёт UA-команда). Отдельный файл и
 * отдельный выключатель: GA — третий потребитель со своим словарём, и склеивать его с пикселями
 * значило бы включать и выключать их только вместе.
 *
 * Словарь — дословно из доки:
 * - каждый экран шлёт своё событие b2b_outrch_scrN («1 экран = 1 ивент», отчёт "где отваливаются");
 * - рега → lead_b2b («такой ивент уже есть на других лендах»);
 * - покупка → purchase_b2b, value = 92% суммы. Было «то же правило, что у пикселей», но с 26.08.2026
 *   Мета получает 70% (pixels.ts, просьба UA), а здесь осталось 92% — см. REPORTED_SHARE ниже.
 *
 * Говорит только с прод-хоста: режим force аналитики GA не открывает (analyticsGate.marketingAllowed).
 * С 2026-09-28 в FUNNEL_PARAMS ещё и сплит-тест — `funnel_test`, `split_pool` (utils/funnel.ts); оба
 * надо зарегистрировать в GA Admin как измерения уровня события, задним числом они не работают.
 */

import { marketingAllowed } from '@/utils/analyticsGate'
import { FUNNEL_ASSIGNMENT, FUNNEL_PARAMS } from '@/utils/funnel'
import { StorageKeys, storageService } from '@/utils/localStorageService'
import { leadAlreadySent, leadSlot, withLeadSent } from '@/utils/marketingConfig'
import { cleanPageLocation } from '@/utils/thankYouParams'

const GA_ID = (import.meta.env.VITE_GA4_ID as string | undefined) ?? ''

/** Доля суммы в value для GA. ВНИМАНИЕ: с 26.08.2026 у пикселей 70% (просьба UA), здесь — 92% из
    исходной доки: просьба касалась только FB и LinkedIn. Если UA выровняет и GA — менять тут. */
const REPORTED_SHARE = 0.92

/**
 * Экран → номер. Нумерация сквозная по воронке, как в примере доки (scr1, scr2, …): лендинг открывает
 * счёт, шаги визарда — это маршруты, воркспейс закрывает. Новый экран получает СЛЕДУЮЩИЙ номер, а не
 * вставку в середину: у UA на номерах строятся отчёты, и переезд нумерации молча ломает их историю.
 */
const SCREENS: readonly [RegExp, number][] = [
  [/^\/$/, 1],
  [/^\/onboard/, 2],
  [/^\/app\/product/, 3],
  [/^\/app\/roles/, 4],
  [/^\/app\/industries/, 5],
  [/^\/app\/locations/, 6],
  [/^\/app\/company/, 7],
  [/^\/app\/launch/, 8],
  [/^\/thank-you/, 9],
  [/^\/home/, 10],
  /* Регистрация почтой — вход воронки 4 (2026-09-22). СЛЕДУЮЩИЙ номер, а не вставка после лендинга,
     по правилу выше: у UA на номерах стоят отчёты. В воронке 4 порядок экранов 1 → 11 → 10: поиск,
     пейволл и чекаут идут внутри /home и для GA — один экран scr10, как и середина воронки 3;
     читать их только по событиям Amplitude с funnel_type. */
  [/^\/signup/, 11],
]

/**
 * Номер экрана по маршруту, или null у маршрута вне воронки. Экспортируется, потому что этой же
 * нумерацией метится page_viewed в Amplitude: два отчёта, считающие «третий экран» по-разному —
 * это два отчёта, которые невозможно свести, а UA строит свои именно на этих номерах.
 */
export const screenNumber = (pathname: string): number | null =>
  SCREENS.find(([pattern]) => pattern.test(pathname))?.[1] ?? null

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let started = false

const start = () => {
  if (started) return
  started = true
  window.dataLayer = window.dataLayer ?? []
  // Канонический снипет: gtag пушит arguments в dataLayer, скрипт вычитывает его непрерывно —
  // у GA, в отличие от Меты, очередь живая всегда, здесь та граблина не повторится.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  window.gtag('js', new Date())
  /* send_page_view: false — страницы шлём сами на каждый SPA-переход; авто-отправка дала бы дубль
     первой страницы и тишину на остальных. */
  window.gtag('config', GA_ID, { send_page_view: false })
  const tag = document.createElement('script')
  tag.async = true
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(tag)
}

/**
 * АДРЕС СТРАНИЦЫ — ЯВНО И В КАЖДОМ СОБЫТИИ, а не тот, что gtag возьмёт из адреса сам (2026-09-28).
 * /thank-you приходит от шлюза с почтой и user_id покупателя в query, и хотя main.tsx чистит адрес до
 * рендера (utils/thankYouParams.ts), адрес для GA собирается ещё раз из чистого правила
 * (cleanPageLocation) — второй замок на том же месте. В каждом событии, а не только в page_view:
 * событие без page_location gtag метит текущим адресом, и purchase_b2b — ровно то, что уходит с
 * /thank-you. Query других экранов остаётся: по utm_* и gclid GA атрибутирует сессию рекламе.
 */
const here = () => ({ page_location: cleanPageLocation(window.location.href) })

/** Экран открыт: страница для GA и поэкранное событие для отчёта по шагам. */
export const gaScreen = (pathname: string) => {
  if (!GA_ID || !marketingAllowed()) return
  start()
  window.gtag?.('event', 'page_view', { page_path: pathname, ...here(), ...FUNNEL_PARAMS })
  const screen = screenNumber(pathname)
  if (screen) window.gtag?.('event', `b2b_outrch_scr${screen}`, { ...here(), ...FUNNEL_PARAMS })
}

/**
 * Рега — тот же момент, что Lead у пикселей: контакт онбординга принят. И то же правило: один раз на
 * браузер и пару «воронка × тест» (utils/marketingConfig.ts) — lead_b2b ключевое событие, на нём
 * учится Google Ads, и повторная отправка формы не второй лид.
 */
export const gaLead = () => {
  if (!GA_ID || !marketingAllowed()) return
  const slot = leadSlot(FUNNEL_ASSIGNMENT)
  const sent = storageService.getItem<unknown>(StorageKeys.adLeads)
  if (leadAlreadySent(sent, 'ga', slot)) return
  start()
  window.gtag?.('event', 'lead_b2b', { ...here(), ...FUNNEL_PARAMS })
  storageService.setItem(StorageKeys.adLeads, withLeadSent(sent, 'ga', slot))
}

/**
 * Покупка подтверждена (на /thank-you, после настоящего списания). transaction_id — их order_id.
 * `funnel_at_entry` — воронка из записи поиска, как у пикселя (pixels.ts, pixelsPurchase).
 */
export const gaPurchase = (totalUsd: number, orderId: string | null, funnelAtEntry: number | null) => {
  if (!GA_ID || !marketingAllowed()) return
  start()
  window.gtag?.('event', 'purchase_b2b', {
    value: Math.round(totalUsd * REPORTED_SHARE * 100) / 100,
    currency: 'USD',
    transaction_id: orderId ?? undefined,
    ...here(),
    ...FUNNEL_PARAMS,
    ...(funnelAtEntry ? { funnel_at_entry: funnelAtEntry } : {}),
  })
}
